import { getStrings } from "../../core/i18n";

// Regex-based instead of the old document.createElement("div")/textContent/innerHTML trick —
// that allocated a throwaway DOM node on every call, and this runs once per line of every
// message on every message-list re-render (renderMarkdownLite in chat.ts). Only escapes what a
// browser's own text-node serializer would (&, <, >) — quotes don't need escaping outside an
// attribute value; escapeAttr below handles that separate case.
export function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Wires the hover/click/outside-click behavior for any ".info-icon-wrap > .info-icon" +
// ".info-bubble" pair, regardless of what built the markup — providerForm.ts's infoIcon()
// (plain escaped text) and chat.ts's topbar token-usage popup (richer, hand-built HTML with
// live numbers) both use this same wiring, moved here 2026-08-10 so it's not duplicated between
// the two. Hover shows the bubble via plain CSS :hover (auto-hides on mouseleave, no JS needed
// for that half); click pins it open until a click lands outside the wrap, handled here.
export function wireInfoIcons(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>(".info-icon-wrap").forEach((wrap) => {
    const btn = wrap.querySelector(".info-icon") as HTMLButtonElement;
    function closeOnOutsideClick(e: MouseEvent): void {
      if (!wrap.contains(e.target as Node)) {
        wrap.classList.remove("is-open");
        document.removeEventListener("click", closeOnOutsideClick);
      }
    }
    btn.addEventListener("click", (e) => {
      // Otherwise this same click immediately reaches the document listener just added below
      // and closes the bubble it was meant to open.
      e.stopPropagation();
      const isOpen = wrap.classList.toggle("is-open");
      if (isOpen) {
        document.addEventListener("click", closeOnOutsideClick);
      } else {
        document.removeEventListener("click", closeOnOutsideClick);
      }
    });
  });
}

// Compact "?" replacement for a field's long-form explanation — hover shows the bubble (plain
// CSS :hover, so it auto-hides on mouseleave with no JS); click pins it open until a click
// elsewhere (wireInfoIcons above handles that half). Used by providerForm.ts (provider-level
// Temperature/Max context/System prompt/Max reply length) and sessionSwitcher.ts's Edit session
// form (the same four, at the per-chat override level) — moved here 2026-08-11 so both forms
// share one copy instead of two drifting ones.
export function infoIcon(text: string): string {
  return `<span class="info-icon-wrap"><button type="button" class="info-icon" aria-label="${getStrings().domUtils.moreInfoAria}">?</button><div class="info-bubble">${escapeHtml(text)}</div></span>`;
}

// labelWithInfo wraps the caption and the icon in a flex row with align-items:center — NOT
// vertical-align, which anchors near the text baseline/x-height rather than the visual center
// and made the icon read as sitting low no matter how it was tuned. Flexbox centers the two
// boxes against each other directly, which is what "level with the text" actually needs.
export function labelWithInfo(caption: string, infoText: string): string {
  return `<span class="label-row"><span>${caption}</span>${infoIcon(infoText)}</span>`;
}

function applyInlineFormatting(escaped: string): string {
  const bolded = escaped.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");
  return bolded.replace(/\*([^*]+?)\*/g, "<em>$1</em>");
}

// Marks each line of a passage as a quote line for renderMarkdownLite below, matching pairing:
// used when attaching Word-selected text to an outgoing message (see chat.ts) so it renders as
// a set-off block instead of a raw "quoted" string.
export function toQuoteBlock(text: string): string {
  return text
    .split("\n")
    .map((line) => (line ? `> ${line}` : ">"))
    .join("\n");
}

// Optional collapse affordance for a quote block — used for the attached-selection passage in
// a sent (user) message bubble, so a long selection doesn't dominate the chat history. Not used
// for assistant replies (their own collapse control is the whole-bubble footer arrow, a
// different mechanism — see chat.ts). `key` identifies this specific quote block for the
// click-to-toggle wiring in chat.ts; `isExpanded` is read fresh on every render, not owned here.
export interface CollapsibleQuoteOptions {
  key: string;
  isExpanded: boolean;
}

// A deliberately small, custom subset of Markdown (bold/italic, plus "> "-prefixed quote lines)
// rather than a full parser library — AI replies commonly come back with **bold**/*italic* and
// showing the raw asterisks looks broken, but a full Markdown renderer is more weight than this
// narrow use case justifies. Always escapes HTML first, so this is safe to use on untrusted AI
// text. Quote lines (see toQuoteBlock) render as a set-off block rather than literal "> " characters.
export function renderMarkdownLite(
  text: string,
  collapsibleQuote?: CollapsibleQuoteOptions
): string {
  const htmlLines: string[] = [];
  let quoteBuffer: string[] = [];

  function flushQuote(): void {
    if (quoteBuffer.length === 0) return;
    if (collapsibleQuote) {
      const { key, isExpanded } = collapsibleQuote;
      const s = getStrings();
      const label = isExpanded ? s.chat.collapseSelectedText : s.chat.expandSelectedText;
      htmlLines.push(
        `<div class="quoted-block${isExpanded ? "" : " is-collapsed"}">` +
          `<div class="quoted-block-text">${quoteBuffer.join("<br>")}</div>` +
          `<button type="button" class="quote-collapse-btn" data-action="toggle-quote" data-key="${escapeAttr(key)}" title="${label}" aria-label="${label}" aria-expanded="${isExpanded ? "true" : "false"}">` +
          `<svg class="quote-collapse-icon${isExpanded ? " is-up" : ""}" width="10" height="10" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 4.5l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>` +
          `</button>` +
          `</div>`
      );
    } else {
      htmlLines.push(`<div class="quoted-block">${quoteBuffer.join("<br>")}</div>`);
    }
    quoteBuffer = [];
  }

  for (const rawLine of text.split("\n")) {
    const isQuoteLine = rawLine === ">" || rawLine.startsWith("> ");
    if (isQuoteLine) {
      const quoted = rawLine === ">" ? "" : rawLine.slice(2);
      quoteBuffer.push(applyInlineFormatting(escapeHtml(quoted)));
    } else {
      flushQuote();
      htmlLines.push(applyInlineFormatting(escapeHtml(rawLine)));
    }
  }
  flushQuote();

  return htmlLines.join("<br>");
}
